import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DOCUMENT, inject, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ContactB2B } from '@components/form/components/form-layout-2/model';
import { IsStepVisiblePipe } from '@components/form/components/form-layout-2/pipe';
import { ContactB2BService } from '@components/form/components/form-layout-2/services';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { FormItemComponent } from '@shared/components/form-item';
import { FormAbstractComponent } from '@shared/components/form-item/abstract';
import { FormItem } from '@shared/components/form-item/model';
import { TitleComponent } from '@shared/components/title';
import { Title } from '@shared/components/title/model';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-form-layout-2',
  templateUrl: './form-layout-2.component.html',
  styleUrl: './form-layout-2.component.scss',
  imports: [TitleComponent, FormItemComponent, IsStepVisiblePipe, ButtonComponent],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate('0.2s ease-in', style({ opacity: 1 }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormLayout2Component extends FormAbstractComponent implements OnDestroy {
  specs = signal<FormItem[]>([
    {
      type: 'TEXT',
      key: 'fullname',
      placeholder: 'saisir vos nom et prénom',
      autocomplete: 'off',
      rules: {
        regexp: /^([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+\s+([A-Za-zÀ-ÖØ-öø-ÿ]+[-\s]?)+$/,
        tips: 'Nom et prénom invalides',
      },
    },
    {
      type: 'TEXT',
      key: 'project',
      autocomplete: 'off',
      placeholder: `Décrivez, ici, brièvement votre projet`,
      rules: {
        min: 1,
        tips: `La description est obligatoire`,
      },
    },
    {
      type: 'TEXT',
      key: 'city',
      placeholder: 'saisir une ville',
      autocomplete: 'off',
      rules: {
        min: 1,
        tips: 'Saisie obligatoire',
      },
    },
    {
      type: 'TEXT',
      key: 'phone',
      autocomplete: 'off',
      placeholder: 'saisir votre numéro de téléphone',
      rules: {
        regexp: /^0[1-9]\s?\d{2}\s?\d{2}\s?\d{2}\s?\d{2}$/,
        tips: 'Numéro de téléphone invalide',
      },
    },
    {
      type: 'TEXT',
      key: 'email',
      autocomplete: 'off',
      placeholder: 'saisir votre adresse email',
      rules: {
        regexp: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        tips: 'Adresse email invalide',
      },
    },
  ]);
  breakpoints = inject(ResponsiveService).breakpoints;
  isSending = signal(false);
  isSendFailed = signal(false);
  readonly title = signal<Title>({
    level: 'H1',
    text: 'Parlons de vous et de votre projet professionnel !',
    size: {
      mobile: 'H1',
      desktop: 'H1',
    },
    color: 'WHITE',
  });
  readonly #contactB2BService = inject(ContactB2BService);
  readonly #logsService = inject(LogsService);
  readonly #router = inject(Router);
  readonly #destroy$: Subject<boolean> = new Subject<boolean>();
  readonly #window = inject<Document>(DOCUMENT).defaultView as Window;

  constructor() {
    super();
  }

  onSuccess() {
    if (this.isSending()) return;
    if (!this.isSending() && this.isValidForm()) {
      this.isSending.set(true);
      this.isSendFailed.set(false);
      const toSaved = { ...this.form, type: '/contact_types/contact_b2b' } as unknown as ContactB2B;
      this.#contactB2BService
        .create(toSaved, true)
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: () => {
            void this.#router.navigate([`${this.#window.location.pathname}/confirmation`]);
            this.isSending.set(false);
          },
          error: (error: unknown) => {
            this.#logsService.error('FormLayout2Component', 'Error while saving B2B contact', error);
            this.isSending.set(false);
            this.isSendFailed.set(true);
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.#destroy$.next(true);
    this.#destroy$.complete();
  }
}
